import type { VNode } from 'vue'
import { NButton, NCollapse, NCollapseItem, NTooltip } from 'naive-ui'
import { useClipboard, useShare } from '@vueuse/core'
import { machineryAccessoryCategoryToGerman, offerTypesToGerman } from '../translations'
import type { ApiInboundLogisticsTaskById, ApiInboundLogisticsTaskForDriver, ApiItemSetGetById, ApiItemSetPublicGetById, ApiLogisticsTaskGetById, ApiMachineryAccessoryGetById, ApiMachineryGetById, ApiOfferGetById, ApiOfferPositionGetById, ApiPublicMachineryAccessoryGetById, ApiPublicTransportationCompanyDriveGetLogisticsTaskByCuid, ApiReceptionMachineryGetById, DispatcherTaskForApp, MachineryAccessoryCategory, MachineryAggregatedPricingDecisionData, MachineryCategories, MachineryPartnerReleasePricingData, OfferType } from '~/types'
import { ExternalSalesMarketplaceIcon, NuxtLink } from '#components'
import LogisticsPositionOverviewCell from '~/components/Logistics/PositionOverviewCell.vue'

const { share, isSupported: isShareSupported } = useShare()
const { copy, isSupported: isCopySupported } = useClipboard()

export interface OneDataColumn<T> {
  title: string
  key: keyof T
  minWidth?: number
  dataMinWidth?: number
  isHeading?: boolean
  render?: (arg: T) => unknown
  renderComponent?: (arg: T) => VNode
  isVisible?: (arg: T) => boolean
  rowProps?: Record<string, unknown>
  hideIfNullish?: boolean
}

const renderBoolean = (value: boolean | undefined | null) => value === true ? 'Ja' : value === false ? 'Nein' : ''

export default function () {
  const i18n = useI18n()

  /**
   * Find the storage location of the positions belonging to a logistics task.
   *
   * This function will return the _first_ storage location it finds for any machinery or machinery accessory.
   */
  function getStorageLocationFromLogisticsTask(task: ApiLogisticsTaskGetById | ApiPublicTransportationCompanyDriveGetLogisticsTaskByCuid) {
    for (const position of task.positionsToShip) {
      const storageLocation = position.machinery?.storageLocation || position.machineryAccessory?.storageLocation
      if (storageLocation) {
        return storageLocation
      }
    }
  }

  const {
    machineryFieldsByMachineCategory: showMachineryFieldsByMachineCategory,
    machineryPropulsionFieldsByMachineCategory: showMachineryPropulsionFieldsByMachineCategory,
    machineryAccessoryFieldsByCategory: showMachineryAccessoryFieldsByCategory,
    machineryAccessoryPublicFieldsByCategory: showMachineryAccessoryPublicFieldsByCategory,
  } = useFormOptionalConditions()

  const machineryPrice: OneDataColumn<ApiMachineryGetById>[] = [
    {
      title: i18n.t('machinery.field.pricePurchaseEuros.name'),
      key: 'pricePurchaseEuros',
      render: ({ pricePurchaseEuros }) => pricePurchaseEuros.toLocaleString('de-DE'),
    },
    {
      title: i18n.t('common.field.paymentCondition.name'),
      key: 'paymentCondition',
    },
    {
      title: i18n.t('machinery.field.additionalDeliveryCost.name'),
      key: 'additionalDeliveryCost',
      render: ({ additionalDeliveryCost }) => additionalDeliveryCost?.toLocaleString('de-DE'),
    },
    {
      title: i18n.t('machinery.field.relatedOfferPositions.name'),
      key: 'relatedOfferPositions',
      render: ({ relatedOfferPositions }) => relatedOfferPositions
        .filter((position): position is typeof position & { repurchasePrice: number, repurchasedAt: Date } => typeof position.repurchasePrice === 'number' && position.repurchasedAt instanceof Date)
        .sort((a, b) => a.repurchasedAt.getTime() - b.repurchasedAt.getTime())
        .map(position => `${position.repurchasePrice.toLocaleString('de-DE')} am ${useDateAsString(position.repurchasedAt, 'dd.MM.yyyy')}`)
        .join(', '),
      hideIfNullish: true,
    },
  ]

  const machinerySalePrice: OneDataColumn<ApiMachineryGetById>[] = [
    {
      title: i18n.t('machinery.field.priceRecommendedToSellFor.name'),
      key: 'priceRecommendedToSellFor',
      render: ({ priceRecommendedToSellFor }) => priceRecommendedToSellFor?.toLocaleString('de-DE'),
    },
    {
      title: i18n.t('machinery.field.endCustomerSellingPrice.name', { currency: i18n.t('unit.eur') }),
      key: 'endCustomerSellingPrice',
      render: ({ endCustomerSellingPrice }) => endCustomerSellingPrice?.toLocaleString('de-DE'),
    },
    {
      title: i18n.t('machinery.field.dealerSellingPrice.name', { currency: i18n.t('unit.eur') }),
      key: 'dealerSellingPrice',
      render: ({ dealerSellingPrice }) => dealerSellingPrice?.toLocaleString('de-DE'),
    },
    {
      title: i18n.t('machinery.field.releasedForSaleOnExternalMarketplaces.name'),
      key: 'releasedForSaleOnExternalMarketplaces',
      renderComponent: ({ releasedForSaleOnExternalMarketplaces }) => {
        const renderedIcons = releasedForSaleOnExternalMarketplaces.map(({ marketplaceId }) => {
          return h(NTooltip, { trigger: 'hover' }, {
            default: () => i18n.t(`externalSalesMarketplace.id.${marketplaceId}`),
            trigger: () => h(ExternalSalesMarketplaceIcon, { marketplaceId, class: 'h-[20px]' }),
          })
        })
        return h('div', { class: 'flex gap-2 items-center' }, renderedIcons)
      },
      isVisible: ({ releasedForSaleOnExternalMarketplaces }) => releasedForSaleOnExternalMarketplaces.length > 0,
    },
  ]

  const machineryRelease: OneDataColumn<ApiMachineryGetById>[] = [
    {
      title: i18n.t('machinery.field.isForeignReleasedForRent.name'),
      key: 'isForeignReleasedForRent',
      render: ({ isForeignReleasedForRent }) => isForeignReleasedForRent ? 'Ja' : 'Nein',
      isVisible: ({ isForeignMachinery }) => isForeignMachinery,
    },
    {
      title: i18n.t('machinery.field.isReleasedForRent.name'),
      key: 'isReleasedForRent',
      render: ({ isReleasedForRent }) => isReleasedForRent ? 'Ja' : 'Nein',
    },
    {
      title: i18n.t('machinery.field.isReleasedForSale.name'),
      key: 'isReleasedForSale',
      render: ({ isReleasedForSale }) => isReleasedForSale ? 'Ja' : 'Nein',
      isVisible: ({ isForeignMachinery }) => !isForeignMachinery,
    },
    {
      title: i18n.t('machinery.field.isReleasedForOnlineSale.name'),
      key: 'isReleasedForOnlineSale',
      render: ({ isReleasedForOnlineSale }) => isReleasedForOnlineSale ? 'Ja' : 'Nein',
      isVisible: ({ isForeignMachinery }) => !isForeignMachinery,
    },
    {
      title: i18n.t('machinery.releasedForSaleOnExternalMarketplaces.label'),
      key: 'releasedForSaleOnExternalMarketplaces',
      renderComponent: ({ releasedForSaleOnExternalMarketplaces }) => {
        const renderedIcons = releasedForSaleOnExternalMarketplaces.map(({ marketplaceId }) => {
          return h(NTooltip, { trigger: 'hover' }, {
            default: () => i18n.t(`externalSalesMarketplace.id.${marketplaceId}`),
            trigger: () => h(ExternalSalesMarketplaceIcon, { marketplaceId, class: 'h-[20px]' }),
          })
        })
        return h('div', { class: 'flex gap-2 items-center' }, renderedIcons.length > 0 ? renderedIcons : i18n.t('general.no'))
      },
      isVisible: ({ isForeignMachinery }) => !isForeignMachinery,
    },
  ]

  const machineryAggregatedPricingDecisionData: OneDataColumn<MachineryAggregatedPricingDecisionData>[] = [
    {
      title: i18n.t('machinery.table.pricePurchaseEuros.title'),
      key: 'pricePurchaseEuros',
      render: ({ pricePurchaseEuros }) => typeof pricePurchaseEuros === 'number' ? pricePurchaseEuros.toLocaleString('de-DE') : i18n.t('general.notApplicable'),
    },
    {
      title: i18n.t('receptionMachinery.operatingHours.label'),
      key: 'operatingHours',
      render: ({ operatingHours }) => typeof operatingHours === 'number' ? operatingHours.toLocaleString('de-DE') : i18n.t('general.notApplicable'),
    },
    {
      title: i18n.t('machinery.internalCostPositions.sum.title'),
      key: 'internalCostPositionsPriceSum',
      render: ({ internalCostPositionsPriceSum: internalCostPositionsSumEuros }) => typeof internalCostPositionsSumEuros === 'number' ? internalCostPositionsSumEuros.toLocaleString('de-DE') : i18n.t('general.notApplicable'),
    },
    {
      title: i18n.t('reporting.machineryRevenue.title'),
      key: 'totalMachineryRevenue',
      render: ({ totalMachineryRevenue: totalMachineryRevenueEuros }) => typeof totalMachineryRevenueEuros === 'number' ? totalMachineryRevenueEuros.toLocaleString('de-DE') : i18n.t('general.notApplicable'),
    },
  ]

  const machineryPartnerReleasePricingData: OneDataColumn<MachineryPartnerReleasePricingData>[] = [
    {
      title: i18n.t('onlineSales.endCustomerSellingPriceWithCurrency.label', { currency: 'EUR' }),
      key: 'endCustomerSellingPrice',
      render: ({ endCustomerSellingPrice }) => typeof endCustomerSellingPrice === 'number' ? endCustomerSellingPrice.toLocaleString('de-DE') : i18n.t('general.notApplicable'),
    },
    {
      title: i18n.t('onlineSales.dealerSellingPriceWithCurrency.label', { currency: 'EUR' }),
      key: 'dealerSellingPrice',
      render: ({ dealerSellingPrice }) => typeof dealerSellingPrice === 'number' ? dealerSellingPrice.toLocaleString('de-DE') : i18n.t('general.notApplicable'),
    },
  ]

  const storageLocationColumn: OneDataColumn<{ storageLocation?: string | null }>[] = [
    {
      title: i18n.t('machinery.field.storageLocation.name'),
      key: 'storageLocation',
      render: ({ storageLocation }) => storageLocation ?? 'N/A',
    },
  ]

  const receptionMachineryVehicle: OneDataColumn<ApiReceptionMachineryGetById>[] = [
    {
      title: i18n.t('machinery.field.yearBuilt.name'),
      key: 'yearBuilt',
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.chassisNumber.name'),
      key: 'chassisNumber',
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.serialNumber.name'),
      key: 'serialNumber',
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.product.name'),
      key: 'product',
      hideIfNullish: true,
    },
    {
      title: i18n.t('machinery.field.drive.name'),
      key: 'driveId',
      render: ({ drive }) => drive ? i18n.t(`machinery.drive.${drive.name}`) : undefined,
      hideIfNullish: true,
    },
    {
      title: i18n.t('machinery.field.liftingWeight.name'),
      key: 'liftingWeight',
      render: ({ liftingWeight }) => liftingWeight?.toLocaleString('de-DE'),
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'liftingWeight', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.basketLoad.name'),
      key: 'basketLoad',
      render: ({ basketLoad }) => basketLoad?.toLocaleString('de-DE'),
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'basketLoad', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.tractionForce.name'),
      key: 'tractionForce',
      render: ({ tractionForce }) => tractionForce?.toLocaleString('de-DE'),
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'tractionForce', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.loadCenter.name'),
      key: 'loadCenter',
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'loadCenter', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.opticalCondition.name'),
      key: 'opticalCondition',
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.technicalCondition.name'),
      key: 'technicalCondition',
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.weight.name'),
      key: 'weight',
      render: ({ weight }) => weight?.toLocaleString('de-DE'),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.totalWeight.name'),
      key: 'weight',
      render: ({ weight, battery }) => ((weight ?? 0) + (battery?.weight ?? 0)).toLocaleString('de-DE'),
      isVisible: ({ weight, battery }) => !!weight && !!battery?.weight,
    },
    {
      title: i18n.t('common.field.type.name'),
      key: 'type',
      render: ({ type }) => type?.name,
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.typeAddition.name'),
      key: 'typeAddition',
    },
    {
      title: i18n.t('receptionMachinery.field.operatingHours.name'),
      key: 'initialOperatingHours',
      render: ({ initialOperatingHours }) => initialOperatingHours?.toLocaleString('de-DE'),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.key.name'),
      key: 'key',
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.initialLift.name'),
      key: 'initialLift',
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'initialLift', category as MachineryCategories),
      render: ({ initialLift }) => renderBoolean(initialLift),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.seats.name'),
      key: 'seats',
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'seats', category as MachineryCategories),
      hideIfNullish: true,
    },
  ]

  const receptionMachineryPlatform: OneDataColumn<ApiReceptionMachineryGetById>[] = [
    {
      title: i18n.t('common.field.length.name'),
      key: 'platformLengthInMillimeters',
      render: ({ platformLengthInMillimeters }) => platformLengthInMillimeters?.toLocaleString('de-DE'),
      hideIfNullish: true,
    },
    {
      title: i18n.t('common.field.width.name'),
      key: 'platformWidthInMillimeters',
      render: ({ platformWidthInMillimeters }) => platformWidthInMillimeters?.toLocaleString('de-DE'),
      hideIfNullish: true,
    },
    {
      title: i18n.t('common.field.height.name'),
      key: 'platformHeightInMillimeters',
      render: ({ platformHeightInMillimeters }) => platformHeightInMillimeters?.toLocaleString('de-DE'),
      hideIfNullish: true,
    },
  ]

  const receptionMachineryMast: OneDataColumn<ApiReceptionMachineryGetById>[] = [
    {
      title: i18n.t('receptionMachinery.field.mast.name'),
      key: 'mast',
      render: ({ mast }) => mast ? i18n.t(`machinery.mast.${mast}`) : i18n.t('common.notAvailable'),
      hideIfNullish: true,
    },
    {
      title: i18n.t('machinery.field.workHeight.name'),
      key: 'workHeightInMillimeters',
      render: ({ workHeightInMillimeters }) => workHeightInMillimeters?.toLocaleString('de-DE'),
      hideIfNullish: true,
    },
    {
      title: i18n.t('machinery.field.liftingHeight.name'),
      key: 'liftingHeightInMillimeters',
      render: ({ liftingHeightInMillimeters }) => liftingHeightInMillimeters?.toLocaleString('de-DE'),
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'liftingHeightInMillimeters', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.overallHeight.name'),
      key: 'mastOverallHeightInMillimeters',
      render: ({ mastOverallHeightInMillimeters }) => mastOverallHeightInMillimeters?.toLocaleString('de-DE'),
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'mastOverallHeightInMillimeters', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.freeLift.name'),
      key: 'freeLiftInMillimeters',
      render: ({ freeLiftInMillimeters }) => freeLiftInMillimeters?.toLocaleString('de-DE'),
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'freeLiftInMillimeters', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.forkCarriageWidth.name'),
      key: 'forkCarriageWidthInMillimeters',
      render: ({ forkCarriageWidthInMillimeters }) => forkCarriageWidthInMillimeters?.toLocaleString('de-DE'),
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'forkCarriageWidthInMillimeters', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.fem.name'),
      key: 'fem',
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'fem', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.merloExtension.label'),
      key: 'merloExtension',
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'fem', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.miscRubricAdditionalInfo.label'),
      key: 'miscRubricAdditionalInfo',
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'fem', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.hals.name'),
      key: 'hals',
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'hals', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.addOns.name'),
      key: 'addOns',
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'addOns', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.sideReach.name'),
      key: 'sideReachInMillimeters',
      render: ({ sideReachInMillimeters }) => sideReachInMillimeters?.toLocaleString('de-DE'),
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'sideReachInMillimeters', category as MachineryCategories),
      hideIfNullish: true,
    },
  ]

  const receptionMachineryDimension: OneDataColumn<ApiReceptionMachineryGetById>[] = [
    {
      title: i18n.t('receptionMachinery.field.lengthToForkface.name'),
      key: 'lengthToForkfaceInMillimeters',
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'lengthToForkfaceInMillimeters', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.totalLength.name'),
      key: 'totalLengthInMillimeters',
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'totalLengthInMillimeters', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.wheelbase.name'),
      key: 'wheelbaseInMillimeters',
      render: ({ wheelbaseInMillimeters }) => wheelbaseInMillimeters?.toLocaleString('de-DE'),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.overallWidth.name'),
      key: 'overallWidthInMillimeters',
      render: ({ overallWidthInMillimeters }) => overallWidthInMillimeters?.toLocaleString('de-DE'),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.groundClearanceCenter.name'),
      key: 'groundClearanceCenter',
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.cabinHeight.name'),
      key: 'cabinHeightInMillimeters',
      render: ({ cabinHeightInMillimeters }) => cabinHeightInMillimeters?.toLocaleString('de-DE'),
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'cabinHeightInMillimeters', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('common.field.height.name'),
      key: 'dimensionHeightInMillimeters',
      render: ({ dimensionHeightInMillimeters }) => dimensionHeightInMillimeters?.toLocaleString('de-DE'),
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'dimensionHeightInMillimeters', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.overallHeight.name'),
      key: 'overallHeightInMillimeters',
      render: ({ overallHeightInMillimeters }) => overallHeightInMillimeters?.toLocaleString('de-DE'),
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'overallHeightInMillimeters', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.outreach.name'),
      key: 'outreach',
      render: ({ outreach }) => outreach?.toLocaleString('de-DE'),
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'outreach', category as MachineryCategories),
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.hookHeight.name'),
      key: 'hookHeight',
      render: ({ hookHeight }) => hookHeight?.toLocaleString('de-DE'),
      isVisible: ({ category }) => useShowFieldByFilters(showMachineryFieldsByMachineCategory, 'hookHeight', category as MachineryCategories),
      hideIfNullish: true,
    },
  ]

  const receptionMachineryTires: OneDataColumn<ApiReceptionMachineryGetById>[] = [
    {
      title: i18n.t('machinery.field.producerCompanyName.name'),
      key: 'tire',
      render: ({ tire }) => tire?.producerCompanyName,
      isVisible: ({ tire }) => !!tire?.producerCompanyName,
    },
    {
      title: i18n.t('common.field.type.name'),
      key: 'tire',
      render: ({ tire }) => tire?.typeName,
      isVisible: ({ tire }) => !!tire?.typeName,
    },
    {
      title: i18n.t('receptionMachinery.tire.field.color.name'),
      key: 'tire',
      render: ({ tire }) => tire?.color,
      isVisible: ({ tire }) => !!tire?.color,
    },
    {
      title: i18n.t('receptionMachinery.tire.field.tireType.name'),
      key: 'tire',
      render: ({ tire }) => tire?.receptionMachineryTireTireTypeRelation?.receptionMachineryTireTypes?.map(type => type.typeName).join(', '),
      isVisible: ({ tire }) => !!tire?.receptionMachineryTireTireTypeRelation?.receptionMachineryTireTypes,
    },
    {
      title: i18n.t('receptionMachinery.tire.field.tireCount.name'),
      key: 'tire',
      render: ({ tire }) => tire?.tireCount,
      isVisible: ({ tire }) => !!tire?.tireCount,
    },
    {
      title: i18n.t('receptionMachinery.tire.field.rear.name'),
      key: 'tire',
      isHeading: true,
      hideIfNullish: true,
    },
    {
      title: i18n.t('common.field.size.name'),
      key: 'tire',
      render: ({ tire }) => tire?.rearSize,
      isVisible: ({ tire }) => !!tire?.rearSize,
    },
    {
      title: i18n.t('common.field.count.name'),
      key: 'tire',
      render: ({ tire }) => tire?.rearCount,
      isVisible: ({ tire }) => !!tire?.rearCount,
    },
    {
      title: i18n.t('common.field.condition.name'),
      key: 'tire',
      render: ({ tire }) => tire?.rearCondition,
      isVisible: ({ tire }) => !!tire?.rearCondition,
    },
    {
      title: i18n.t('receptionMachinery.tire.field.front.name'),
      key: 'tire',
      isHeading: true,
      hideIfNullish: true,
    },
    {
      title: i18n.t('common.field.size.name'),
      key: 'tire',
      render: ({ tire }) => tire?.frontSize,
      isVisible: ({ tire }) => !!tire?.frontSize,
    },
    {
      title: i18n.t('common.field.count.name'),
      key: 'tire',
      render: ({ tire }) => tire?.frontCount,
      isVisible: ({ tire }) => !!tire?.frontCount,
    },
    {
      title: i18n.t('common.field.condition.name'),
      key: 'tire',
      render: ({ tire }) => tire?.frontCondition,
      isVisible: ({ tire }) => !!tire?.frontCondition,
    },
  ]

  const receptionMachineryMotor: OneDataColumn<ApiReceptionMachineryGetById>[] = [
    {
      title: i18n.t('receptionMachinery.motor.field.producerCompanyName.name'),
      key: 'motor',
      render: ({ motor }) => motor?.producerCompanyName,
      isVisible: ({ motor, category }) => !!motor?.producerCompanyName && useShowFieldByFilters(showMachineryPropulsionFieldsByMachineCategory, 'name', category as MachineryCategories),
    },
    {
      title: i18n.t('receptionMachinery.motor.field.power.name'),
      key: 'motor',
      render: ({ motor }) => motor?.power?.toLocaleString('de-DE'),
      isVisible: ({ motor }) => !!motor?.power,
    },
    {
      title: i18n.t('common.field.type.name'),
      key: 'motor',
      render: ({ motor }) => motor?.typeName,
      isVisible: ({ motor, category }) => !!motor?.typeName && useShowFieldByFilters(showMachineryPropulsionFieldsByMachineCategory, 'typeName', category as MachineryCategories),
    },
    {
      title: i18n.t('receptionMachinery.motor.field.gear.name'),
      key: 'motor',
      render: ({ motor }) => motor?.gear,
      isVisible: ({ motor, category }) => !!motor?.gear && useShowFieldByFilters(showMachineryPropulsionFieldsByMachineCategory, 'gear', category as MachineryCategories),
    },
    {
      title: i18n.t('receptionMachinery.motor.field.serialNumber.name'),
      key: 'serialNumber',
      render: ({ motor }) => motor?.serialNumber,
      isVisible: ({ motor, category }) => !!motor?.serialNumber && useShowFieldByFilters(showMachineryPropulsionFieldsByMachineCategory, 'serialNumber', category as MachineryCategories),
    },
    {
      title: i18n.t('receptionMachinery.motor.field.gearProducer.name'),
      key: 'motor',
      render: ({ motor }) => motor?.gearProducer,
      isVisible: ({ motor, category }) => !!motor?.gearProducer && useShowFieldByFilters(showMachineryPropulsionFieldsByMachineCategory, 'gearProducer', category as MachineryCategories),
    },
    {
      title: i18n.t('receptionMachinery.motor.field.hasAdBlue.name'),
      key: 'motor',
      render: ({ motor }) => motor?.hasAdBlue ? 'Ja' : 'Nein',
      isVisible: ({ motor, category }) => motor?.hasAdBlue !== null && useShowFieldByFilters(showMachineryPropulsionFieldsByMachineCategory, 'hasAdBlue', category as MachineryCategories),
    },
  ]

  const receptionMachineryBattery: OneDataColumn<ApiReceptionMachineryGetById>[] = [
    {
      title: i18n.t('receptionMachinery.field.producerCompanyName.name'),
      key: 'battery',
      render: ({ battery }) => battery?.producerCompanyName,
      isVisible: ({ battery }) => !!battery?.producerCompanyName,
    },
    {
      title: i18n.t('common.field.type.name'),
      key: 'battery',
      render: ({ battery }) => battery?.typeName,
      isVisible: ({ battery }) => !!battery?.typeName,
    },
    {
      title: i18n.t('common.field.weight.name'),
      key: 'battery',
      render: ({ battery }) => battery?.weight?.toLocaleString('de-DE'),
      isVisible: ({ battery }) => !!battery?.weight,
    },
    {
      title: i18n.t('receptionMachinery.field.volt.name'),
      key: 'battery',
      render: ({ battery }) => battery?.volt,
      isVisible: ({ battery }) => !!battery?.volt,
    },
    {
      title: i18n.t('machinery.field.yearBuilt.name'),
      key: 'battery',
      render: ({ battery }) => battery?.yearBuilt,
      isVisible: ({ battery }) => !!battery?.yearBuilt,
    },
    {
      title: i18n.t('machinery.field.serialNumber.name'),
      key: 'battery',
      render: ({ battery }) => battery?.serialNumber,
      isVisible: ({ battery }) => !!battery?.serialNumber,
    },
    {
      title: i18n.t('receptionMachinery.battery.field.ampere.name'),
      key: 'battery',
      render: ({ battery }) => battery?.ampere && `${battery.ampere}A`,
      isVisible: ({ battery }) => !!battery?.ampere,
    },
  ]

  const receptionMachinerySteeringType: OneDataColumn<ApiReceptionMachineryGetById>[] = [
    {
      title: i18n.t('receptionMachinery.field.steeringType.name'),
      key: 'steeringType',
      hideIfNullish: true,
    },
  ]

  const receptionMachineryExtraInfo: OneDataColumn<ApiReceptionMachineryGetById>[] = [
    {
      title: i18n.t('receptionMachinery.field.addOns.name'),
      key: 'extraAddOns',
      hideIfNullish: true,
    },
    {
      title: i18n.t('receptionMachinery.field.extraAccessories.name'),
      key: 'extraAccessories',
      hideIfNullish: true,
    },
  ]

  const machineryAccessoryBaseInformation: OneDataColumn<ApiMachineryAccessoryGetById>[] = [
    {
      title: i18n.t('common.field.id.name'),
      key: 'id',
    },
    {
      title: i18n.t('common.field.status.name'),
      key: 'status',
      render: ({ status }) => i18n.t(`machineryAccessory.status.${status}`),
    },
    {
      title: i18n.t('itemSet.name', { count: 1 }),
      key: 'itemSets',
      renderComponent: ({ itemSets }) => {
        const itemSet = itemSets[0]
        if (!itemSet) {
          return h('div', i18n.t('common.notAvailable'))
        }

        const nuxtLinkComponent = h(NuxtLink, {
          class: 'hover:text-primary underline',
          to: `/set/${itemSet.id}`,
        }, () => [
          useRenderIcon({ name: useBrandIcons().set, class: 'mr-1' }),
          h('span', itemSet.title),
        ])

        return h('div', { class: 'flex gap-x-1' }, [
          nuxtLinkComponent,
          `(${itemSet.id})`,
        ])
      },
    },
  ]

  const machineryAccessoryPurchasePriceInformation: OneDataColumn<ApiMachineryAccessoryGetById>[] = [
    {
      title: i18n.t('machinery.field.pricePurchaseEuros.name'),
      key: 'pricePurchaseEuros',
      render: ({ pricePurchaseEuros }) => pricePurchaseEuros?.toLocaleString('de-DE'),
    },
  ]

  const machineryAccessoryInspectionDate: OneDataColumn<ApiMachineryAccessoryGetById>[] = [
    {
      title: i18n.t('machinery.field.technicalInspectionDate.name'),
      key: 'technicalInspectionDate',
      isVisible: ({ category }) => showMachineryAccessoryFieldsByCategory.technicalInspectionDate.includes(category as MachineryAccessoryCategory),
      render: ({ technicalInspectionDate }) => technicalInspectionDate ? useDateAsString(technicalInspectionDate, 'dd.MM.yyyy') : i18n.t('common.notAvailable'),
    },
    {
      title: i18n.t('machinery.field.securityInspectionDate.name'),
      key: 'securityInspectionDate',
      isVisible: ({ category }) => showMachineryAccessoryFieldsByCategory.securityInspectionDate.includes(category as MachineryAccessoryCategory),
      render: ({ securityInspectionDate }) => securityInspectionDate ? useDateAsString(securityInspectionDate, 'dd.MM.yyyy') : i18n.t('common.notAvailable'),
    },
    {
      title: i18n.t('machinery.field.specialPermissions.name'),
      key: 'specialPermissions',
      isVisible: ({ specialPermissions }) => specialPermissions.length > 0,
      render: ({ specialPermissions }) => specialPermissions
        .map(sp => `${sp.title} (${useDateAsString(sp.date, 'dd.MM.yyyy')})`)
        .join(', '),
    },
  ]

  const machineryAccessoryPublicCreationInformation: OneDataColumn<ApiPublicMachineryAccessoryGetById>[] = [
    {
      title: i18n.t('common.field.category.name'),
      key: 'category',
      render: ({ category }) => machineryAccessoryCategoryToGerman[category as MachineryAccessoryCategory],
    },
    {
      title: i18n.t('machinery.field.producerCompanyName.name'),
      key: 'producerCompanyName',
      isVisible: ({ category }) => showMachineryAccessoryPublicFieldsByCategory.producerCompanyName.includes(category as MachineryAccessoryCategory),
    },
    {
      title: i18n.t('common.field.type.name'),
      key: 'typeName',
      isVisible: ({ category }) => showMachineryAccessoryPublicFieldsByCategory.typeName.includes(category as MachineryAccessoryCategory),
    },
    {
      title: i18n.t('machinery.field.yearBuilt.name'),
      key: 'yearBuilt',
      isVisible: ({ category }) => showMachineryAccessoryPublicFieldsByCategory.yearBuilt.includes(category as MachineryAccessoryCategory),
    },
    {
      title: i18n.t('machinery.field.liftingWeight.name'),
      key: 'liftingWeight',
      render: ({ liftingWeight }) => liftingWeight?.toLocaleString('de-DE'),
      isVisible: ({ category }) => showMachineryAccessoryPublicFieldsByCategory.liftingWeight.includes(category as MachineryAccessoryCategory),
    },
    {
      title: i18n.t('common.field.length.name'),
      key: 'lengthInMillimeters',
      render: ({ lengthInMillimeters }) => lengthInMillimeters?.toLocaleString('de-DE'),
      isVisible: ({ category }) => showMachineryAccessoryPublicFieldsByCategory.lengthInMillimeters.includes(category as MachineryAccessoryCategory),
    },
    {
      title: i18n.t('common.field.width.name'),
      key: 'widthInMillimeters',
      render: ({ widthInMillimeters }) => widthInMillimeters?.toLocaleString('de-DE'),
      isVisible: ({ category }) => showMachineryAccessoryPublicFieldsByCategory.widthInMillimeters.includes(category as MachineryAccessoryCategory),
    },
    {
      title: i18n.t('common.field.height.name'),
      key: 'heightInMillimeters',
      render: ({ heightInMillimeters }) => heightInMillimeters?.toLocaleString('de-DE'),
      isVisible: ({ category }) => showMachineryAccessoryPublicFieldsByCategory.heightInMillimeters.includes(category as MachineryAccessoryCategory),
    },
    {
      title: i18n.t('machineryAccessory.field.distanceInnerEdgeToInnerEdge.name'),
      key: 'distanceInnerEdgeToInnerEdge',
      render: ({ distanceInnerEdgeToInnerEdge }) => distanceInnerEdgeToInnerEdge?.toLocaleString('de-DE'),
      isVisible: ({ category }) => showMachineryAccessoryPublicFieldsByCategory.distanceInnerEdgeToInnerEdge.includes(category as MachineryAccessoryCategory),
    },
    {
      title: i18n.t('machineryAccessory.field.distanceOuterEdgeToOuterEdge.name'),
      key: 'distanceOuterEdgeToOuterEdge',
      render: ({ distanceOuterEdgeToOuterEdge }) => distanceOuterEdgeToOuterEdge?.toLocaleString('de-DE'),
      isVisible: ({ category }) => showMachineryAccessoryPublicFieldsByCategory.distanceOuterEdgeToOuterEdge.includes(category as MachineryAccessoryCategory),
    },
    {
      title: i18n.t('machineryAccessory.field.retractionLugsWidth.name'),
      key: 'retractionLugsWidth',
      render: ({ retractionLugsWidth }) => retractionLugsWidth?.toLocaleString('de-DE'),
      isVisible: ({ category }) => showMachineryAccessoryPublicFieldsByCategory.retractionLugsWidth.includes(category as MachineryAccessoryCategory),
    },
    {
      title: i18n.t('machineryAccessory.field.retractionLugsHeight.name'),
      key: 'retractionLugsHeight',
      render: ({ retractionLugsHeight }) => retractionLugsHeight?.toLocaleString('de-DE'),
      isVisible: ({ category }) => showMachineryAccessoryPublicFieldsByCategory.retractionLugsHeight.includes(category as MachineryAccessoryCategory),
    },
    {
      title: i18n.t('machineryAccessory.field.retractionLugsDistanceInnerEdgeToInnerEdge.name'),
      key: 'retractionLugsDistanceInnerEdgeToInnerEdge',
      render: ({ retractionLugsDistanceInnerEdgeToInnerEdge }) => retractionLugsDistanceInnerEdgeToInnerEdge?.toLocaleString('de-DE'),
      isVisible: ({ category }) => showMachineryAccessoryPublicFieldsByCategory.retractionLugsDistanceInnerEdgeToInnerEdge.includes(category as MachineryAccessoryCategory),
    },
    {
      title: i18n.t('machineryAccessory.field.retractionLugsDistanceOuterEdgeToOuterEdge.name'),
      key: 'retractionLugsDistanceOuterEdgeToOuterEdge',
      render: ({ retractionLugsDistanceOuterEdgeToOuterEdge }) => retractionLugsDistanceOuterEdgeToOuterEdge?.toLocaleString('de-DE'),
      isVisible: ({ category }) => showMachineryAccessoryPublicFieldsByCategory.retractionLugsDistanceOuterEdgeToOuterEdge.includes(category as MachineryAccessoryCategory),
    },
    {
      title: i18n.t('machineryAccessory.field.lengthLeverLink.name'),
      key: 'lengthLeverLink',
      render: ({ lengthLeverLink }) => lengthLeverLink?.toLocaleString('de-DE'),
      isVisible: ({ category }) => showMachineryAccessoryPublicFieldsByCategory.lengthLeverLink.includes(category as MachineryAccessoryCategory),
    },
    {
      title: i18n.t('machineryAccessory.field.widthLeverLink.name'),
      key: 'widthLeverLink',
      render: ({ widthLeverLink }) => widthLeverLink?.toLocaleString('de-DE'),
      isVisible: ({ category }) => showMachineryAccessoryPublicFieldsByCategory.widthLeverLink.includes(category as MachineryAccessoryCategory),
    },
    {
      title: i18n.t('machineryAccessory.field.powerLeverLink.name'),
      key: 'powerLeverLink',
      render: ({ powerLeverLink }) => powerLeverLink?.toLocaleString('de-DE'),
      isVisible: ({ category }) => showMachineryAccessoryPublicFieldsByCategory.powerLeverLink.includes(category as MachineryAccessoryCategory),
    },
  ]

  const logisticsTaskGeneralInformation: OneDataColumn<ApiLogisticsTaskGetById>[] = [
    {
      title: 'ID',
      key: 'id',
      minWidth: 200,
    },
    {
      title: 'Art',
      key: 'type',
      render: task => i18n.t(`logisticsTask.type.${task.type}`),
    },
    {
      title: 'Liefer-Datum (Auftrag)',
      key: 'offer',
      render: (task) => {
        if (task.type === 'outbound') {
          return task.offer?.deliveryAt ? useDateAsString(task.offer.deliveryAt) : 'N/A'
        }
        const obligationActuallyEndedAt = task.offer?.obligationActuallyEndedAt
        return obligationActuallyEndedAt ? useDateAsString(obligationActuallyEndedAt) : 'N/A'
      },
      isVisible: task => !!task.offer && task.type !== 'inbound' && task.offer?.type !== 'special',
    },
    {
      title: 'Abhol-Datum (Auftrag)',
      key: 'offer',
      render: (task) => {
        const obligationActuallyEndedAt = task.offer?.obligationActuallyEndedAt
        return obligationActuallyEndedAt ? useDateAsString(obligationActuallyEndedAt) : 'N/A'
      },
      isVisible: task => !!task.offer && task.type === 'inbound' && task.offer?.type !== 'special',
    },
    {
      title: 'Liefer-Datum (Logistik)',
      key: 'deliveryAt',
      render: ({ deliveryAt }) => deliveryAt ? useDateAsString(deliveryAt) : 'N/A',
      isVisible: task => task.type !== 'inbound',
    },
    {
      title: 'Abhol-Datum (Logistik)',
      key: 'deliveryAt',
      render: ({ deliveryAt }) => deliveryAt ? useDateAsString(deliveryAt) : 'N/A',
      isVisible: task => task.type === 'inbound',
    },
    {
      title: 'Typ',
      key: 'offer',
      render: ({ offer }) => offer ? offerTypesToGerman[offer.type as OfferType] : 'Unbekannt',
      isVisible: task => !!task.offer,
    },
    {
      title: 'Abholung Kunden-Kennzeichen',
      key: 'offer',
      render: ({ offer }) => offer ? offer.customerLicensePlate : 'Unbekannt',
      isVisible: task => task.doesCustomerDoTask,
    },
    {
      title: 'Lagerort',
      key: 'positionsToShip',
      render: task => getStorageLocationFromLogisticsTask(task),
      isVisible: (task) => {
        if (!task.offer || task.offer?.type === 'special') {
          return false
        }
        return Boolean(getStorageLocationFromLogisticsTask(task))
      },
    },
    {
      title: 'Lieferort',
      key: 'offer',
      render: (task) => {
        if (['aToB', 'defect'].includes(task.type)) {
          return task.deliveryTo ?? 'N/A'
        }
        return task.offer?.deliveryLocation ?? 'N/A'
      },
      isVisible: task => task.type !== 'inbound',
    },
    {
      title: 'Koordinaten genauer Abstellort',
      key: 'offer',
      renderComponent: ({ offer }) => {
        if (!offer?.deliveryAddress?.lat || !offer?.deliveryAddress?.lng) {
          return h('div', 'N/A')
        }

        const cordinates = `${offer.deliveryAddress.lat},${offer.deliveryAddress.lng}`
        const googleMapsLinks = useGoogleMapsLink(cordinates)
        return h('a', { class: 'underline', href: googleMapsLinks, target: '_blank' }, cordinates)
      },
      isVisible: (task) => {
        if (!task.offer) {
          return false
        }
        const isOutbound = task.type === 'outbound'
        const doesOfferHaveCoordinates = task.offer.deliveryAddress?.lat && task.offer.deliveryAddress.lng
        return Boolean(isOutbound && doesOfferHaveCoordinates && !task.offer.deliveryAddress?.hideCoordinates)
      },
    },
    {
      title: 'Abholort',
      key: 'offer',
      render: (task) => {
        if (['aToB', 'defect'].includes(task.type)) {
          return task.deliveryFrom ?? 'N/A'
        }
        return task.offer?.deliveryLocation ?? 'N/A'
      },
      isVisible: task => ['inbound', 'aToB', 'defect'].includes(task.type),
    },
    {
      title: 'Abweichende Abholadresse',
      key: 'offer',
      render: task => task.differentPickupLocation ?? 'N/A',
      isVisible: ({ type }) => type === 'inbound',
    },
    {
      title: '1. Ansprechpartner',
      key: 'offer',
      render: task => task.offer?.claimingPartnerName ?? 'N/A',
      isVisible: task => !!task.offer,
    },
    {
      title: '1. Ansprechpartner Email',
      key: 'offer',
      render: task => task.offer?.claimingPartnerEmail ?? 'N/A',
      isVisible: task => !!task.offer,
    },
    {
      title: '1. Ansprechpartner Telefon',
      key: 'offer',
      render: task => task.offer?.claimingPartnerTelephone ?? 'N/A',
      isVisible: task => !!task.offer,
    },
    {
      title: '2. Ansprechpartner',
      key: 'offer',
      render: task => task.offer?.secondClaimingPartnerName,
      isVisible: task => !!task.offer?.secondClaimingPartnerName,
    },
    {
      title: '2. Ansprechpartner Email',
      key: 'offer',
      render: task => task.offer?.secondClaimingPartnerEmail || 'N/A',
      isVisible: task => !!task.offer?.secondClaimingPartnerName,
    },
    {
      title: '2. Ansprechpartner Telefon',
      key: 'offer',
      render: task => task.offer?.secondClaimingPartnerTelephone,
      isVisible: task => !!task.offer?.secondClaimingPartnerName,
    },
    {
      title: '3. Ansprechpartner',
      key: 'offer',
      render: task => task.offer?.thirdClaimingPartnerName,
      isVisible: task => !!task.offer?.thirdClaimingPartnerName,
    },
    {
      title: '3. Ansprechpartner Email',
      key: 'offer',
      render: task => task.offer?.thirdClaimingPartnerEmail || 'N/A',
      isVisible: task => !!task.offer?.thirdClaimingPartnerName,
    },
    {
      title: '3. Ansprechpartner Telefon',
      key: 'offer',
      render: task => task.offer?.thirdClaimingPartnerTelephone,
      isVisible: task => !!task.offer?.thirdClaimingPartnerName,
    },
    {
      title: 'Geräte-ID',
      key: 'defect',
      render: ({ defect }) => defect?.machineryId,
      isVisible: task => !!task.defect?.machineryId,
    },
    {
      title: 'Anbaugeräte-ID',
      key: 'defect',
      render: ({ defect }) => defect?.machineryAccessoryId,
      isVisible: task => !!task.defect?.machineryAccessoryId,
    },
    {
      title: 'Auftrag',
      key: 'offer',
      renderComponent: ({ offer }) => {
        return offer
          ? h('div', { class: 'flex flex-col' }, [offer.id, h(NButton, {
              size: 'tiny',
              onClick: () => window.open(`/api/pdf/offer/details/${offer.cuid}`, '_blank'),
            }, () => [useRenderIcon({ name: 'material-symbols:description-outline-rounded', class: 'mr-2' }), 'Download Auftrag'])])
          : h('div', 'N/A')
      },
      isVisible: task => !!task.offer,
    },
  ]

  const logisticsTaskGeneralInformationForDriver = computed((): OneDataColumn<ApiLogisticsTaskGetById | ApiPublicTransportationCompanyDriveGetLogisticsTaskByCuid>[] => [
    {
      title: 'ID',
      key: 'id',
      minWidth: 200,
    },
    {
      title: i18n.t('logisticsTask.type.label'),
      key: 'type',
      render: task => i18n.t(`logisticsTask.type.${task.type}`),
    },
    {
      title: i18n.t('logisticsTask.deliveryDate.order.label'),
      key: 'offer',
      render: (task) => {
        if (task.type === 'outbound') {
          return task.offer?.deliveryAt ? useDateAsString(task.offer.deliveryAt) : 'N/A'
        }
        const obligationActuallyEndedAt = task.offer?.obligationActuallyEndedAt
        return obligationActuallyEndedAt ? useDateAsString(obligationActuallyEndedAt) : 'N/A'
      },
      isVisible: task => !!task.offer && task.offer?.type !== 'special',
    },
    {
      title: i18n.t('logisticsTask.deliveryDate.logistics.label'),
      key: 'deliveryAt',
      render: ({ deliveryAt }) => deliveryAt ? useDateAsString(deliveryAt) : 'N/A',
    },
    {
      title: i18n.t('logisticsTask.offer.type.label'),
      key: 'offer',
      render: ({ offer }) => offer ? i18n.t(`offer.type.${offer.type}.name`) : i18n.t('common.unknown'),
      isVisible: task => !!task.offer,
    },
    {
      title: i18n.t('logisticsTask.offer.doesCustomerDoTask.label'),
      key: 'offer',
      render: ({ offer }) => offer ? offer.customerLicensePlate : i18n.t('common.unknown'),
      isVisible: task => task.doesCustomerDoTask,
    },
    {
      title: i18n.t('logisticsTask.storageLocation.label'),
      key: 'positionsToShip',
      render: task => getStorageLocationFromLogisticsTask(task),
      isVisible: task => !!task.offer && Boolean(getStorageLocationFromLogisticsTask(task)),
    },
    {
      title: i18n.t('logisticsTask.placeOfDelivery.label'),
      key: 'offer',
      renderComponent: (task) => {
        const deliveryLocation = ['aToB', 'defect'].includes(task.type) ? task.deliveryTo : task.offer?.deliveryLocation
        return h('span', { class: 'font-bold text-primary' }, deliveryLocation ?? 'N/A')
      },
    },
    {
      title: i18n.t('logisticsTask.parkingLocationCoordination.label'),
      key: 'offer',
      renderComponent: ({ offer }) => {
        if (!offer?.deliveryAddress?.lat || !offer?.deliveryAddress?.lng) {
          return h('div', 'N/A')
        }

        const cordinates = `${offer.deliveryAddress.lat},${offer.deliveryAddress.lng}`
        const googleMapsLinks = useGoogleMapsLink(cordinates)
        return h('a', { class: 'underline', href: googleMapsLinks, target: '_blank' }, cordinates)
      },
      isVisible: (task) => {
        if (!task.offer) {
          return false
        }
        const isOutbound = task.type === 'outbound'
        const doesOfferHaveCoordinates = task.offer.deliveryAddress?.lat && task.offer.deliveryAddress.lng
        return Boolean(isOutbound && doesOfferHaveCoordinates && !task.offer.deliveryAddress?.hideCoordinates)
      },
    },
    {
      title: i18n.t('logisticsTask.pickupLocation.label'),
      key: 'offer',
      renderComponent: task => h('span', { class: 'font-bold text-primary' }, task.deliveryFrom ?? 'N/A'),
      isVisible: task => ['aToB', 'defect'].includes(task.type),
    },
    {
      title: i18n.t('logisticsTask.differentPickupLocation.label'),
      key: 'offer',
      renderComponent: task => h('span', { class: 'font-bold text-primary' }, task.differentPickupLocation ?? 'N/A'),
      isVisible: ({ type, offer }) => type === 'inbound' && !!offer,
    },
    {
      title: `1. ${i18n.t('offer.field.claimingPartnerName.name')}`,
      key: 'offer',
      render: task => task.offer?.claimingPartnerName ?? 'N/A',
      isVisible: task => !!task.offer,
    },
    {
      title: `1. ${i18n.t('offer.field.claimingPartnerTelephone.name')}`,
      key: 'offer',
      renderComponent: task => task.offer?.claimingPartnerTelephone
        ? h('a', { class: 'underline text-primary', href: `tel:${task.offer?.claimingPartnerTelephone}` }, task.offer?.claimingPartnerTelephone)
        : h('span', 'N/A'),
      isVisible: task => !!task.offer,
    },
    {
      title: `1. ${i18n.t('offer.field.claimingPartnerEmail.name')}`,
      key: 'offer',
      renderComponent: task => task.offer?.claimingPartnerEmail
        ? h('a', { class: 'underline text-primary', href: `mailto:${task.offer?.claimingPartnerEmail}` }, task.offer?.claimingPartnerEmail)
        : h('span', 'N/A'),
      isVisible: task => !!task.offer,
    },
    {
      title: `2. ${i18n.t('offer.field.claimingPartnerName.name')}`,
      key: 'offer',
      render: task => task.offer?.secondClaimingPartnerName,
      isVisible: task => !!task.offer?.secondClaimingPartnerName,
    },
    {
      title: `2. ${i18n.t('offer.field.claimingPartnerTelephone.name')}`,
      key: 'offer',
      renderComponent: task => task.offer?.secondClaimingPartnerTelephone
        ? h('a', { class: 'underline text-primary', href: `tel:${task.offer?.secondClaimingPartnerTelephone}` }, task.offer?.secondClaimingPartnerTelephone)
        : h('span', 'N/A'),
      isVisible: task => !!task.offer?.secondClaimingPartnerName,
    },
    {
      title: `2. ${i18n.t('offer.field.claimingPartnerEmail.name')}`,
      key: 'offer',
      renderComponent: task => task.offer?.secondClaimingPartnerEmail
        ? h('a', { class: 'underline text-primary', href: `tel:${task.offer?.secondClaimingPartnerEmail}` }, task.offer?.secondClaimingPartnerEmail)
        : h('span', 'N/A'),
      isVisible: task => !!task.offer?.secondClaimingPartnerName,
    },
    {
      title: `3. ${i18n.t('offer.field.claimingPartnerName.name')}`,
      key: 'offer',
      render: task => task.offer?.thirdClaimingPartnerName,
      isVisible: task => !!task.offer?.thirdClaimingPartnerName,
    },
    {
      title: `3. ${i18n.t('offer.field.claimingPartnerTelephone.name')}`,
      key: 'offer',
      renderComponent: task => task.offer?.thirdClaimingPartnerTelephone
        ? h('a', { class: 'underline text-primary', href: `tel:${task.offer?.thirdClaimingPartnerTelephone}` }, task.offer?.thirdClaimingPartnerTelephone)
        : h('span', 'N/A'),
      isVisible: task => !!task.offer?.thirdClaimingPartnerName,
    },
    {
      title: `3. ${i18n.t('offer.field.claimingPartnerEmail.name')}`,
      key: 'offer',
      renderComponent: task => task.offer?.thirdClaimingPartnerEmail
        ? h('a', { class: 'underline text-primary', href: `tel:${task.offer?.thirdClaimingPartnerEmail}` }, task.offer?.thirdClaimingPartnerEmail)
        : h('span', 'N/A'),
      isVisible: task => !!task.offer?.thirdClaimingPartnerName,
    },
    {
      title: i18n.t('offer.field.machineryId.name'),
      key: 'defect',
      render: ({ defect }) => defect?.machineryId,
      isVisible: task => !!task.defect?.machineryId,
    },
    {
      title: i18n.t('offer.field.machineryAccessoryId.name'),
      key: 'defect',
      render: ({ defect }) => defect?.machineryAccessoryId,
      isVisible: task => !!task.defect?.machineryAccessoryId,
    },
  ])

  const getNuxtLinkComponent = (to: string, title?: string | null) => h(NuxtLink, { class: 'hover:text-primary underline', to }, () => [useRenderIcon({ name: useBrandIcons().machinery, class: 'mr-1' }), h('span', title ?? 'N/A')])

  const logisticsTaskPositionsOverview = computed((): OneDataColumn<ApiLogisticsTaskGetById['positionsToShip'] | ApiPublicTransportationCompanyDriveGetLogisticsTaskByCuid['positionsToShip']>[] => [
    {
      title: i18n.t('logisticsTask.positions.machine.label'),
      key: 'toString',
      renderComponent: positionsToShip => h(LogisticsPositionOverviewCell, { positionsToShip, type: 'machinery' }),
      isVisible: positionsToShip => positionsToShip.some(position => position.type === 'machinery'),
    },
    {
      title: i18n.t('logisticsTask.positions.attachments.label'),
      key: 'toString',
      renderComponent: positionsToShip => h(LogisticsPositionOverviewCell, { positionsToShip, type: 'machineryAccessory' }),
      isVisible: positionsToShip => positionsToShip.some(position => position.type === 'machineryAccessory'),
    },
    {
      title: i18n.t('logisticsTask.positions.sets.label'),
      key: 'toString',
      renderComponent: (positionsToShip) => {
        const positions = positionsToShip.filter(position => position.type === 'itemSet')
        return positions
          ? h('div', { class: 'flex flex-col' }, positions.map(({ itemSetId, itemSetMember }) => {
              return h('div', { class: 'flex gap-x-1 flex-col' }, [
                getNuxtLinkComponent(`/set/${itemSetId}`, itemSetId),
                h(NCollapse, { class: 'mt-1 mb-2 ml-2' }, () => h(NCollapseItem, { title: i18n.t('logisticsTask.positions.attachments.label') }, () => h('div', { class: 'flex gap-2' }, itemSetMember.map(({ machineryAccessoryId }) => getNuxtLinkComponent(`/machinery-accessory/${machineryAccessoryId}`, machineryAccessoryId))))),
              ])
            }))
          : h('div', 'N/A')
      },
      isVisible: positionsToShip => positionsToShip.some(position => position.type === 'itemSet'),
    },
  ])

  const aToBLogisticsTaskPositionOverview: OneDataColumn<ApiLogisticsTaskGetById['positionsToShip'][number]>[] = [
    {
      title: 'Titel',
      key: 'title',
    },
  ]

  const logisticsPlanningTaskOverview: OneDataColumn<DispatcherTaskForApp | ApiOfferGetById['logisticsTasks'][number]>[] = [
    {
      title: 'Typ',
      key: 'id',
      render: ({ offer }) => offer ? offerTypesToGerman[offer.type as OfferType] : 'Unbekannt',
    },
    {
      title: 'Lieferort',
      key: 'offer',
      render: ({ offer }) => offer?.deliveryLocation ?? 'N/A',
    },
    {
      title: 'Lagerort',
      key: 'id',
      render: task => getStorageLocationFromLogisticsTask(task as ApiLogisticsTaskGetById),
      isVisible: task => Boolean(getStorageLocationFromLogisticsTask(task as ApiLogisticsTaskGetById)),
    },
    {
      title: 'Liefer-Datum (Auftrag)',
      key: 'offer',
      render: ({ offer }) => offer?.deliveryAt ? useDateAsString(offer?.deliveryAt) : 'N/A',
    },
    {
      title: i18n.t('logisticsTask.transportationCompanyName.label'),
      key: 'transportationCompanyName',
      isVisible: ({ transportationCompanyName }) => Boolean(transportationCompanyName),
    },
  ]

  const deliveryChecklist: OneDataColumn<ApiLogisticsTaskGetById | ApiPublicTransportationCompanyDriveGetLogisticsTaskByCuid>[] = [
    {
      title: 'Standort der übergebenen Geräte',
      key: 'deliveryLocation',
      renderComponent: (task) => {
        const { deliveryLocation } = task

        if (!deliveryLocation) {
          return h('span', 'N/A')
        }

        const googleMapsLink = useGoogleMapsLink(deliveryLocation)

        const shareButton = h(NButton, { size: 'tiny', onClick: () => share({ url: googleMapsLink }) }, useRenderIcon({ name: 'material-symbols:ios-share' }))
        const mailButton = h(NButton, { size: 'tiny', onClick: () => navigateTo(getMailtoForLogisticsEmail(task, googleMapsLink), { external: true }) }, useRenderIcon({ name: 'material-symbols:mail-outline' }))
        const copyButton = h(NButton, { size: 'tiny', onClick: () => copy(googleMapsLink) }, useRenderIcon({ name: 'material-symbols:content-copy-outline' }))

        return h('div', { class: 'flex items-center gap-2' }, [
          h('a', { href: googleMapsLink, target: '_blank', class: 'text-blue-500 hover:underline' }, deliveryLocation),
          ...(isShareSupported.value ? [shareButton] : []),
          mailButton,
          ...(isCopySupported.value ? [copyButton] : []),
        ])
      },
      isVisible: ({ deliveryLocation }) => Boolean(deliveryLocation),
    },
    {
      title: 'Eingewiesene Person',
      key: 'deliveryPerson',
      render: ({ deliveryPerson }) => deliveryPerson ?? 'N/A',
      isVisible: ({ deliveryPerson }) => Boolean(deliveryPerson),
    },
    {
      title: 'Bemerkungen',
      key: 'deliveryNotes',
      render: ({ deliveryNotes }) => deliveryNotes ?? 'N/A',
      isVisible: ({ deliveryNotes }) => Boolean(deliveryNotes),
    },
  ]

  const issuancePopupInformation: OneDataColumn<ApiOfferPositionGetById>[] = [
    {
      title: 'Auftrag',
      key: 'id',
      render: ({ relatedOffer }) => relatedOffer.id,
    },
    {
      title: 'Geräte Nr.',
      key: 'id',
      render: ({ machinery }) => machinery?.id,
      isVisible: ({ machinery }) => Boolean(machinery),
    },
    {
      title: 'Gerätetyp',
      key: 'id',
      render: ({ machinery }) => machinery?.type.name,
      isVisible: ({ machinery }) => Boolean(machinery),
    },
    {
      title: 'Hersteller',
      key: 'id',
      render: ({ machinery }) => machinery?.producerCompanyName,
      isVisible: ({ machinery }) => Boolean(machinery),
    },
    {
      title: 'Letztes UVV-Datum',
      key: 'id',
      render: ({ machinery }) => machinery?.lastUVVDate ? useDateAsString(machinery.lastUVVDate, 'dd.MM.yy') : 'N/A',
      isVisible: ({ machinery }) => Boolean(machinery),
    },
    {
      title: 'Kunde',
      key: 'id',
      render: ({ relatedOffer }) => relatedOffer.customer.name,
    },
    {
      title: 'Gerät',
      key: 'id',
      render: ({ machineryAccessory, machineryAccessoryCategory }) => machineryAccessory ? `${machineryAccessory.id}, ${machineryAccessoryCategoryToGerman[machineryAccessory.category as MachineryAccessoryCategory]}, ${machineryAccessory.producerCompanyName}` : machineryAccessoryCategoryToGerman[machineryAccessoryCategory as MachineryAccessoryCategory],
      isVisible: ({ type }) => ['machineryAccessory', 'machineryAccessoryCategory'].includes(type),
    },
  ]

  const itemSetColumnsPublic: OneDataColumn<ApiItemSetPublicGetById | ApiItemSetGetById>[] = [
    {
      title: 'Id',
      key: 'id',
    },
    {
      title: 'Title',
      key: 'title',
      render: ({ title }) => title,
    },
    {
      title: 'Beschreibung',
      key: 'description',
      render: ({ description }) => description ?? ' ',
    },
  ]

  const itemSetColumns: OneDataColumn<ApiItemSetGetById>[] = [
    ...itemSetColumnsPublic,
    {
      title: 'Lagerplatz',
      key: 'storageLocation',
      render: ({ storageLocation }) => storageLocation ?? 'N/A',
    },
    {
      title: 'Hersteller',
      key: 'producerCompanyName',
      render: ({ producerCompanyName }) => producerCompanyName ?? 'N/A',
    },
    {
      title: 'Typ',
      key: 'typeName',
      render: ({ typeName }) => typeName ?? 'N/A',
    },
    {
      title: 'Tragkraft (kg)',
      key: 'liftingWeightInKilograms',
      render: ({ liftingWeightInKilograms }) => liftingWeightInKilograms?.toLocaleString('de-DE'),
    },
    {
      title: 'Gewicht (kg)',
      key: 'weightInKilograms',
      render: ({ weightInKilograms }) => weightInKilograms?.toLocaleString('de-DE'),
    },
    {
      title: 'Transport-Länge (mm)',
      key: 'transportLengthInMillimeters',
      render: ({ transportLengthInMillimeters }) => transportLengthInMillimeters?.toLocaleString('de-DE'),
    },
    {
      title: 'Transport-Breite (mm)',
      key: 'transportWidthInKilograms',
      render: ({ transportWidthInKilograms }) => transportWidthInKilograms?.toLocaleString('de-DE'),
    },
    {
      title: 'Transport-Höhe (mm)',
      key: 'transportHeightInKilograms',
      render: ({ transportHeightInKilograms }) => transportHeightInKilograms?.toLocaleString('de-DE'),
    },
    {
      title: 'Erstellt am',
      key: 'createdAt',
      render: row => useDateAsString(row.createdAt),
    },
    {
      title: 'Erstellt von',
      key: 'createdBy',
      render: row => row.createdBy.name,
    },
  ]

  const logisticsCompletedInformation: OneDataColumn<ApiLogisticsTaskGetById>[] = [
    {
      title: 'Abgeschlossen am',
      key: 'completedAt',
      render: ({ completedAt }) => completedAt ? useDateAsString(completedAt) : ' ',
    },
    {
      title: 'Abgeschlossen von',
      key: 'completedBy',
      render: ({ completedBy }) => completedBy?.name ?? ' ',
    },
  ]

  const lastUVVDate: OneDataColumn<ApiMachineryGetById | ApiMachineryAccessoryGetById>[] = [
    {
      title: i18n.t('machinery.field.lastUVVDate.name'),
      key: 'lastUVVDate',
      render: ({ lastUVVDate }) => lastUVVDate ? useDateAsString(lastUVVDate, 'dd.MM.yyyy') : 'N/A',
    },
  ]

  const machineryInspectionDate: OneDataColumn<ApiMachineryGetById>[] = [
    {
      title: i18n.t('machinery.field.technicalInspectionDate.name'),
      key: 'technicalInspectionDate',
      render: ({ technicalInspectionDate }) => technicalInspectionDate ? useDateAsString(technicalInspectionDate, 'dd.MM.yyyy') : i18n.t('common.notAvailable'),
    },
    {
      title: i18n.t('machinery.field.securityInspectionDate.name'),
      key: 'securityInspectionDate',
      render: ({ securityInspectionDate }) => securityInspectionDate ? useDateAsString(securityInspectionDate, 'dd.MM.yyyy') : i18n.t('common.notAvailable'),
    },
    {
      title: i18n.t('machinery.field.speedometerInspectionDate.name'),
      key: 'speedometerInspectionDate',
      isVisible: ({ speedometerInspectionDate }) => !!speedometerInspectionDate,
      render: ({ speedometerInspectionDate }) => speedometerInspectionDate && useDateAsString(speedometerInspectionDate, 'dd.MM.yyyy'),
    },
    {
      title: i18n.t('machinery.field.specialPermissions.name'),
      key: 'specialPermissions',
      isVisible: ({ specialPermissions }) => specialPermissions.length > 0,
      render: ({ specialPermissions }) => specialPermissions
        .map(sp => `${sp.title} (${useDateAsString(sp.date, 'dd.MM.yyyy')})`)
        .join(', '),
    },
  ]

  type InboundLogisticsTask = ApiInboundLogisticsTaskById | ApiInboundLogisticsTaskForDriver
type StoragePositionSet = InboundLogisticsTask['storagePositionSets'][number]
const storagePositionSetColumn: OneDataColumn<StoragePositionSet>[] = [
  {
    title: 'Länge (mm)',
    key: 'announcedLengthInMm',
  },
  {
    title: 'Breite (mm)',
    key: 'announcedWidthInMm',
  },
  {
    title: 'Höhe (mm)',
    key: 'announcedHeightInMm',
  },
  {
    title: 'Bruttogewicht (kg)',
    key: 'announcedTotalGrossWeightIncludingLoadCarrierInKg',
  },
]

  type StoragePosition = StoragePositionSet['storagePositions'][number]
  const storagePositionColumn: OneDataColumn<StoragePosition>[] = [
    {
      title: 'Bezeichnung',
      key: 'storageProduct',
      render: ({ storageProduct }) => storageProduct.name,
    },
    {
      title: 'Menge',
      key: 'quantity',
    },
    {
      title: 'Waschen',
      key: 'needsToBeWashedOnArrival',
      render: ({ needsToBeWashedOnArrival }) => needsToBeWashedOnArrival ? 'Ja' : 'Nein',
    },
  ]

  const configs = {
    ...oneDataColumnConfigPublicMachinery(i18n.t),
    machineryPrice,
    machinerySalePrice,
    machineryRelease,
    machineryInspectionDate,
    storageLocationColumn,
    receptionMachineryVehicle,
    receptionMachineryPlatform,
    receptionMachineryDimension,
    receptionMachineryTires,
    receptionMachineryMotor,
    receptionMachineryBattery,
    receptionMachinerySteeringType,
    receptionMachineryExtraInfo,
    machineryAccessoryBaseInformation,
    machineryAccessoryPurchasePriceInformation,
    machineryAccessoryInspectionDate,
    machineryAccessoryPublicCreationInformation,
    machineryAggregatedPricingDecisionData,
    logisticsTaskGeneralInformation,
    machineryPartnerReleasePricingData,
    logisticsTaskGeneralInformationForDriver,
    aToBLogisticsTaskPositionOverview,
    logisticsTaskPositionsOverview,
    logisticsPlanningTaskOverview,
    deliveryChecklist,
    issuancePopupInformation,
    itemSetColumnsPublic,
    itemSetColumns,
    logisticsCompletedInformation,
    lastUVVDate,
    storagePositionSetColumn,
    storagePositionColumn,
    receptionMachineryMast,
  }

  return configs
}
